import React, { useState, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { teamService } from '../../services';
import { useEarningsReminders } from '../../hooks/useEarningsReminders';
import UserControls from './UserControls';

// ============================================
// Icons
// ============================================

const LogoMark = () => (
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

// Three small blocks + a plus in the fourth slot — implies modular,
// composable building blocks (heroicons "squares-plus").
const WidgetsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 002.25-2.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v2.25A2.25 2.25 0 006 10.5zm0 9.75h2.25A2.25 2.25 0 0010.5 18v-2.25a2.25 2.25 0 00-2.25-2.25H6a2.25 2.25 0 00-2.25 2.25V18A2.25 2.25 0 006 20.25zm9.75-9.75H18a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0018 3.75h-2.25A2.25 2.25 0 0013.5 6v2.25a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const DashboardsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M4 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM13 5a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1V5zM4 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1v-5zM13 14a1 1 0 011-1h5a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-5z" />
  </svg>
);

const WorkflowsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const TasksIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

const TeamIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const CoverageIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6zM9 3v15M15 6v15" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M8 7V3M16 7V3M4 11h16M5 5h14a1 1 0 011 1v13a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1z" />
  </svg>
);

// Concentric signal arcs — "listening for events / firing on signals"
// (heroicons "signal")
const TriggersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.807-3.808-9.98 0-13.788m13.788 0c3.808 3.807 3.808 9.98 0 13.788M12 12h.008v.008H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);

// ============================================
// Sidebar nav item
// ============================================

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  prefixMatch?: string;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

interface SidebarNavButtonProps {
  item: NavItem;
  active: boolean;
  expanded: boolean;
}

const SidebarNavButton: React.FC<SidebarNavButtonProps> = ({ item, active, expanded }) => (
  <Link
    to={item.path}
    title={expanded ? undefined : item.label}
    aria-label={item.label}
    className={`
      relative flex items-center h-10 rounded-md transition-colors
      ${expanded ? 'w-full gap-3 px-3 justify-start' : 'w-10 justify-center'}
      ${active
        ? 'bg-primary-50 text-primary-600'
        : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'}
    `}
  >
    <span className="shrink-0 flex items-center justify-center w-5">{item.icon}</span>
    {expanded && <span className="text-sm font-medium truncate">{item.label}</span>}
  </Link>
);

// ============================================
// Full-screen builder detection
// ============================================

/**
 * Routes that take the entire main area (no PageTopBar). On these pages
 * the sidebar shows the user controls cluster at the bottom, since the
 * top bar isn't there to host them.
 */
const FULL_SCREEN_BUILDER_PATHS = [
  '/dashboard-builder',
  '/workflow-builder',
  '/task/',
];

const isFullScreenBuilderRoute = (pathname: string): boolean =>
  FULL_SCREEN_BUILDER_PATHS.some(p => pathname.startsWith(p));

// ============================================
// Sidebar
// ============================================

const Sidebar: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const { user, isAuthenticated } = useAuthStore();
  // Global chrome: surface earnings-reminder socket events as toasts wherever
  // the authed shell is mounted.
  useEarningsReminders();
  const [hasTeam, setHasTeam] = useState<boolean | null>(null);
  // Expandable rail: collapsed = icon-only, expanded = icon + label. Persisted.
  const [expanded, setExpanded] = useState<boolean>(
    () => localStorage.getItem('sidebar:expanded') === '1',
  );
  const toggleExpanded = () =>
    setExpanded(prev => {
      const next = !prev;
      localStorage.setItem('sidebar:expanded', next ? '1' : '0');
      return next;
    });

  const isActive = (path: string) => location.pathname === path;
  const isActivePrefix = (prefix: string) => location.pathname.startsWith(prefix);
  const isItemActive = (item: NavItem) =>
    isActive(item.path) || (item.prefixMatch ? isActivePrefix(item.prefixMatch) : false);

  const isOwner = user?.role === 'owner' || user?.role === 'admin';
  const showUserControlsHere = isFullScreenBuilderRoute(location.pathname);

  // Check team status for workers
  useEffect(() => {
    const checkTeamStatus = async () => {
      if (!isAuthenticated || isOwner) return;

      try {
        const status = await teamService.getTeamStatus();
        setHasTeam(status.hasTeam ?? false);
      } catch (err) {
        // On error leave hasTeam unknown (null) rather than forcing false — false
        // wrongly surfaces "Join Team" (implying no team) on a transient fetch
        // failure. Unknown keeps the rail stable (neither Coverage nor Join Team).
        console.error('Failed to check team status:', err);
      }
    };

    checkTeamStatus();
  }, [isAuthenticated, isOwner]);

  if (!isAuthenticated || isEmbed) return null;

  // Owner navigation, grouped by purpose. Groups render with a divider
  // between them in the rail.
  //   0. Home          — the command-center grid (also reachable via the logo)
  //   1. Build         — libraries / builders for widgets, dashboards, workflows
  //   2. Automation    — triggers that fire workflows on real-world events
  //   3. Org           — people and what they cover
  const ownerNavGroups: NavGroup[] = [
    { items: [
      { path: '/dashboard', label: 'Home', icon: <TasksIcon /> },
    ]},
    { label: 'Build', items: [
      { path: '/widgets', label: 'Widgets', icon: <WidgetsIcon /> },
      { path: '/dashboards', label: 'Dashboards', icon: <DashboardsIcon />, prefixMatch: '/dashboard-builder' },
      { path: '/workflows', label: 'Workflows', icon: <WorkflowsIcon />, prefixMatch: '/workflow-builder' },
    ]},
    { label: 'Automation', items: [
      { path: '/triggers', label: 'Triggers', icon: <TriggersIcon /> },
      { path: '/calendar', label: 'Calendar', icon: <CalendarIcon /> },
    ]},
    { label: 'Org', items: [
      { path: '/team', label: 'Team', icon: <TeamIcon /> },
      { path: '/coverage', label: 'Coverage', icon: <CoverageIcon /> },
    ]},
  ];

  // Worker navigation — single small group, no divider needed.
  const workerNavGroups: NavGroup[] = [
    { items: [
      { path: '/dashboard', label: 'My Tasks', icon: <TasksIcon />, prefixMatch: '/task/' },
      ...(hasTeam ? [{ path: '/coverage', label: 'Coverage', icon: <CoverageIcon /> }] : []),
      { path: '/calendar', label: 'Calendar', icon: <CalendarIcon /> },
      ...(hasTeam === false
        ? [{ path: '/join-team', label: 'Join Team', icon: <TeamIcon /> }]
        : []),
    ]},
  ];

  const navGroups = isOwner ? ownerNavGroups : workerNavGroups;

  return (
    <aside className={`flex flex-col shrink-0 bg-surface z-40 transition-[width] duration-200 ${expanded ? 'w-56' : 'w-14'}`}>
      {/* Logo */}
      <Link
        to="/dashboard"
        className={`flex items-center h-14 shrink-0 border-b border-neutral-200 ${expanded ? 'gap-2 px-3' : 'justify-center'}`}
        aria-label="Orchestrate home"
      >
        <div className="w-8 h-8 bg-primary-600 rounded-md flex items-center justify-center shrink-0">
          <LogoMark />
        </div>
        {expanded && <span className="text-sm font-semibold text-neutral-900 truncate">Westport</span>}
      </Link>

      {/* Primary nav */}
      <nav className={`flex flex-col py-3 flex-1 overflow-y-auto no-scrollbar ${expanded ? 'px-2' : 'items-center'}`}>
        {navGroups.map((group, groupIdx) => (
          <React.Fragment key={groupIdx}>
            {groupIdx > 0 && (
              expanded
                ? <div className="mt-3" aria-hidden="true" />
                : <div className="my-2 h-px w-6 bg-neutral-200" aria-hidden="true" />
            )}
            {expanded && group.label && (
              <div className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                {group.label}
              </div>
            )}
            <div className={`flex flex-col gap-1 ${expanded ? 'w-full' : 'items-center'}`}>
              {group.items.map(item => (
                <SidebarNavButton key={item.path} item={item} active={isItemActive(item)} expanded={expanded} />
              ))}
            </div>
          </React.Fragment>
        ))}
      </nav>

      {/* Expand / collapse toggle */}
      <button
        type="button"
        onClick={toggleExpanded}
        title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        className={`flex items-center h-10 shrink-0 border-t border-neutral-200 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors ${expanded ? 'px-3 gap-3 justify-start' : 'justify-center'}`}
      >
        <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
        </svg>
        {expanded && <span className="text-sm font-medium">Collapse</span>}
      </button>

      {/* Bottom controls — only shown on full-screen builder pages where
          there is no PageTopBar to host the user cluster. */}
      {showUserControlsHere && (
        <div className="py-3 border-t border-neutral-200">
          <UserControls direction="vertical" />
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
